/**
 * Devello SaaS plans (Free vs Pro). Pricing for Pro is $29/mo via Stripe — use STRIPE_PRICE_ID_PRO on the server.
 */

export const PRO_MONTHLY_USD = 29;

const FREE = {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: '',
    description: 'Professional quoting and client organization for every contractor.',
    subtitle: 'Start with the quote builder, client records, and downloadable quote/invoice PDFs.',
    features: [
      'Professional quote builder',
      'Client database',
      'Quote and invoice PDFs',
    ],
    limitations: [],
};

export const PLANS_CONFIG = {
  free: FREE,
  /** Legacy plan ids map to Free tier for UI compatibility */
  starter: { ...FREE, id: 'starter', name: 'Free (legacy Starter)' },
  trial: { ...FREE, id: 'trial', name: 'Free (legacy Trial)' },
  expired: { ...FREE, id: 'expired', name: 'Free' },
  pro: {
    id: 'pro',
    name: 'Pro',
    price: `$${PRO_MONTHLY_USD}`,
    period: '/month',
    description: 'All-in-one contractor management platform for growing teams.',
    subtitle: 'Everything in Free, plus project workspaces, team member portal access, and email delivery from Devello.',
    features: [
      'Everything in Free',
      'Email quote/invoice PDFs from Devello',
      'Team member portal (up to 2 seats)',
      'Project organization',
      'Material price search by city/region',
      'Receipt and file uploads',
      'Change orders and project updates',
      'Daily project notes and logs',
      'Before/after project photos',
      'Job expense tracking',
    ],
    limitations: [],
  },
};

export const PLAN_COMPARISON = {
  features: [
    { name: 'Professional quote builder', free: true, pro: true },
    { name: 'Client database', free: true, pro: true },
    { name: 'Quote and invoice PDFs', free: true, pro: true },
    { name: 'Watermark on quote/invoice PDFs', free: true, pro: false },
    { name: 'Email quote/invoice PDFs from Devello', free: false, pro: true },
    { name: 'Team member portal (up to 2 seats)', free: false, pro: true },
    { name: 'Project organization', free: false, pro: true },
    { name: 'Material price search by city/region', free: false, pro: true },
    { name: 'Receipt and file uploads', free: false, pro: true },
    { name: 'Change orders and project updates', free: false, pro: true },
    { name: 'Daily project notes and logs', free: false, pro: true },
    { name: 'Before/after project photos', free: false, pro: true },
    { name: 'Job expense tracking', free: false, pro: true },
  ],
};

/** Feature keys used by legacy FeatureGate / components */
export const FEATURE_ACCESS = {
  professionalQuotes: ['free', 'pro'],
  markupOverheadTax: ['free', 'pro'],
  invoicePdfCreation: ['free', 'pro'],
  customBranding: ['free', 'pro'],
  clientManagement: ['free', 'pro'],
  teamManagement: ['pro'],
  teamMemberPortal: ['pro'],
  emailQuoteDelivery: ['pro'],
  realtimeMaterialsPricing: ['pro'],
  receiptUpload: ['pro'],
  expenseTracking: ['pro'],
  overviewDashboard: ['pro'],
  projectScheduling: ['pro'],
  advancedAnalytics: ['pro'],
  profitLossTracking: ['pro'],
  projectsWorkspace: ['pro'],
  emailSupport: ['free', 'pro'],
  prioritySupport: ['pro'],
  phoneSupport: ['pro'],
  advancedReporting: ['pro'],
};

/**
 * @param {string} userPlan 'free' | 'pro' | legacy 'starter' (treated as free)
 * @param {string} feature
 */
export function hasFeatureAccess(userPlan, feature) {
  const normalized = userPlan === 'pro' ? 'pro' : 'free';
  const allowed = FEATURE_ACCESS[feature];
  return allowed ? allowed.includes(normalized) : false;
}

export function getPlanDetails(planId) {
  if (!planId || planId === 'starter' || planId === 'trial') return PLANS_CONFIG.free;
  return PLANS_CONFIG[planId] || PLANS_CONFIG.free;
}

export function getAllPlans() {
  return [PLANS_CONFIG.free, PLANS_CONFIG.pro];
}

export default PLANS_CONFIG;
